import {css} from 'aphrodite';
import moment from 'moment-timezone';
import React, {useState} from 'react';

import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  DATEPICKER_CUSTOM_INPUT_USER_EDIT_FORMAL_TIME_FORMATS,
  DATEPICKER_CUSTOM_INPUT_USER_EDIT_INFORMAL_TIME_FORMATS,
} from 'ui-library/inputs/DatetimeField';
import DatetimeInput, {DateInput} from 'ui-library/inputs/DatetimeInput';
import {
  DATETIME_FORMATS,
  PRESENTATIONAL_LOCAL_DATETIME_FORMAT,
  PRESENTATIONAL_TIME_FORMAT,
} from 'util/DateConstants';

import {BENCHLING_TYPE_INPUT_STYLES} from './BenchlingTypeValueInputConstants';
import {ValueInputProps} from './ValueInputTypes';

const DATE_TIME_PICKER_PROPS = {
  validDatetimeInputFormats: DATETIME_FORMATS,
  datetimePresentationalFormat: PRESENTATIONAL_LOCAL_DATETIME_FORMAT,
  timeInputPlaceholder: 'hh=mm AM/PM',
  timeInformalInputFormats: DATEPICKER_CUSTOM_INPUT_USER_EDIT_INFORMAL_TIME_FORMATS,
  timeFormalInputFormats: DATEPICKER_CUSTOM_INPUT_USER_EDIT_FORMAL_TIME_FORMATS,
  timePresentationalFormat: PRESENTATIONAL_TIME_FORMAT,
};

/**
 * @package
 */
export default function DateValueInput(props: ValueInputProps<LeafType.DATE>): JSX.Element {
  const value: string | null = typeof props.valueInput.value === 'string' ? props.valueInput.value : null;

  return (
    <DateInput
      autoFocus={props.allowAutoFocus}
      placeholder="Date"
      className={css(BENCHLING_TYPE_INPUT_STYLES.primitiveValueInput)}
      /**
       * Close the date input's popout calendar as soon as the user selects a new value, rather than after
       * the user clicks outside the popout, because this component is embedded in a own popout that is also
       * closed by the user clicking outside the popout.
       */
      shouldCloseOnSelect={true}
      // Coerce null to '' for display
      value={value ?? ''}
      onChange={(newDateValue: string) => {
        // Coerce '' to null for save
        props.onChange(newDateValue !== '' ? newDateValue : null);
      }}
      isDisabled={props.disabled}
    />
  );
}

function convertDateTimeFromInternalToDisplayFormat(internalValue: string | null): string {
  return internalValue == null ? '' : moment(internalValue).format(PRESENTATIONAL_LOCAL_DATETIME_FORMAT);
}

function convertDateTimeFromDisplayToInternalFormat(displayValue: string): string | null {
  return displayValue === '' ? null : moment(displayValue, DATETIME_FORMATS).toISOString();
}

/**
 * @package
 */
export function DateTimeValueInput(props: ValueInputProps<LeafType.DATETIME>): JSX.Element {
  const internalValue: string | null =
    typeof props.valueInput.value === 'string' ? props.valueInput.value : null;

  const [displayValue, setDisplayValue] = useState<string>(
    convertDateTimeFromInternalToDisplayFormat(internalValue)
  );

  return (
    <DatetimeInput
      autoFocus={props.allowAutoFocus}
      placeholder="Date"
      className={css(BENCHLING_TYPE_INPUT_STYLES.primitiveValueInput)}
      // Coerce null to '' for display
      value={displayValue}
      onChange={(newDisplayValue: string) => {
        setDisplayValue(newDisplayValue);
        props.onChange(convertDateTimeFromDisplayToInternalFormat(newDisplayValue));
      }}
      isDisabled={props.disabled}
      {...DATE_TIME_PICKER_PROPS}
    />
  );
}
